/**
 * Sidebar.tsx
 *
 * CONTEXT SIDEBAR — the navigation rail under the top dock.
 *
 * The old sidebar listed every feature at once. In the dock redesign the top
 * bar carries the modules (see dock.tsx) and this sidebar shows ONLY the
 * sections of the active module — the context you're working in.
 *
 *   - Role-based filtering hides admin-only items from regular employees.
 *   - Module gating hides sections when a module is disabled by super_admin.
 *   - Sections auto-expand when a child route is active.
 *   - On mobile it becomes an overlay with a horizontal module switcher on top.
 *
 * `data-tour="sidebar"` is preserved for the onboarding tour.
 */

import React, { useState, useEffect, memo, useCallback, useMemo, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  // section (parent) icons
  LayoutDashboard, TrendingUp, Users, Briefcase, CalendarDays,
  Settings, ChevronDown, ChevronRight, Building2, BarChart3,
  CheckSquare, LifeBuoy, DollarSign, Crown, Megaphone, Warehouse,
  // child (leaf) icons — every one verified present in lucide-react
  Funnel, Contact, Handshake, Package, Activity, Gauge, Target, LineChart,
  ClipboardCheck, Star, FileText, Network, UserPlus, LogIn, LogOut,
  GraduationCap, CircleUser, CalendarCheck, Calendar, Clock, ShieldCheck,
  DoorOpen, UserX, FileBarChart, Inbox, AlarmClock, Zap, BookOpen, Hash,
  MessageSquare, PieChart, Mail, ListOrdered, Receipt, FileCheck, Scale,
  CreditCard, Wallet, BadgeCheck, ShoppingCart, Truck, ClipboardList, Radio,
  Sun, Lock, Building, Puzzle, ScrollText, Workflow, ListChecks, Timer, GanttChart,
  // footer account menu
  User, LogOut as SignOutIcon, Globe,
} from 'lucide-react';
import { setLocale, getLocale, SUPPORTED_LOCALES, LocaleCode } from '../../i18n';
import { useAuth, useWorkspaceId } from '../../context/AuthContext';
import { Avatar } from '../ui/Avatar';
import { useModules } from '../../context/ModuleContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import { moduleForPath } from '../../lib/moduleRoutes';
import { isSignupEnabled } from '../../lib/signupEnabled';
import { UserRole, ModuleKey } from '../../types';
import AppsRail from './AppsRail';
import ActiveModuleChip from './ActiveModuleChip';
import { useAppsRailCounts } from './useAppsRailCounts';
import {
  DOCK, dockKeyForPath,
} from './dock';
import {
  chooseModuleSection, sectionLabelsForChoice, dockItemAccessible,
  openSectionsWithFallback,
} from '../../lib/sidebarModuleSection';
import { readLastModule, rememberLastModule } from '../../lib/lastModule';
import { ACTIVITY_RECORD_MODULE_KEYS } from '../../lib/activityFeed';

/* Settings-tile colour language (matches the CRM settings tiles + the record
   widgets). Leaf badges use these; anything without an explicit tint inherits
   the ACTIVE MODULE's dock colour so every module reads as one coloured family. */
const TINT = {
  amber:  '#F4B740',
  green:  '#34D399',
  purple: '#A78BFA',
  teal:   '#2DD4BF',
  red:    '#FB7185',
  sky:    '#5EA9F0', // Dashboard
  orange: '#F97316', // Product Catalog
  violet: '#8B5CF6', // Sales Reports (analytics)
  slate:  '#7C8FA8', // Settings — a tinted grey-blue, distinct from plain grey
} as const;

/** Live-count buckets shown on the right of a leaf. */
type CountKey = 'leads' | 'contacts' | 'companies' | 'activitiesOverdue';

interface NavChild {
  label: string;
  path: string;
  roles?: UserRole[];
  /** Optional data-tour attribute value for the onboarding tour. */
  tourAttr?: string;
  /** Leaf icon rendered inside a coloured badge (replaces the old dot bullet). */
  icon?: React.ReactNode;
  /** Explicit badge colour. Falls back to the active module's colour. */
  tint?: string;
  /** Force a neutral (grey) badge, overriding the module-colour fallback. */
  neutral?: boolean;
  /** If set, a live count is shown on the right of this leaf. */
  countKey?: CountKey;
}

interface NavSection {
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: NavChild[];
  roles?: UserRole[];
  /** If set, the section is hidden when this module is disabled. */
  moduleKey?: ModuleKey;
  /** If set, a divider label is rendered above this item */
  groupLabel?: string;
}

/**
 * Full navigation structure — labels are i18n keys (e.g. 'nav.dashboard').
 * The Sidebar calls t(label) to render the translated string.
 * Keys are also used as stable identifiers for the openSections state.
 */
export const NAV: NavSection[] = [
  {
    label: 'nav.dashboard',
    icon: <LayoutDashboard size={18} />,
    path: '/dashboard',
    groupLabel: 'Main',
  },
  {
    label: 'nav.performance',
    icon: <TrendingUp size={18} />,
    moduleKey: 'performance',
    groupLabel: 'Performance',
    children: [
      { label: 'nav.perfDashboard',      path: '/performance/dashboard', icon: <Gauge /> },
      { label: 'nav.companyGoals',       path: '/performance/goals', icon: <Target /> },
      { label: 'nav.deptKpis',           path: '/performance/kpis', icon: <BarChart3 /> },
      { label: 'nav.myKpis',             path: '/performance/my-kpis', icon: <LineChart /> },
      { label: 'nav.weeklyCheckin',      path: '/performance/checkin', icon: <ClipboardCheck /> },
      { label: 'nav.performanceReviews', path: '/performance/reviews', icon: <Star /> },
      { label: 'nav.weeklyReports',      path: '/performance/reports', icon: <FileText />, roles: ['super_admin', 'dept_admin', 'hr_admin', 'team_lead'] },
    ],
  },
  {
    label: 'nav.people',
    icon: <Users size={18} />,
    groupLabel: 'People & HR',
    children: [
      { label: 'nav.employees',   path: '/people/employees', icon: <Users /> },
      { label: 'nav.orgChart',    path: '/people/org-chart', icon: <Network /> },
      { label: 'nav.recruitment', path: '/people/recruitment', icon: <UserPlus /> },
      { label: 'nav.onboarding',  path: '/people/onboarding', icon: <LogIn /> },
      { label: 'nav.offboarding', path: '/people/offboarding', icon: <LogOut /> },
      { label: 'nav.training',    path: '/people/training', icon: <GraduationCap /> },
    ],
  },
  {
    label: 'nav.hr',
    icon: <CalendarDays size={18} />,
    moduleKey: 'hr',
    children: [
      { label: 'nav.myHr',          path: '/hr/my-hr', icon: <CircleUser /> },
      { label: 'nav.myLeave',       path: '/hr/my-leave', icon: <CalendarCheck /> },
      { label: 'nav.teamLeave',     path: '/hr/team-leave', icon: <CalendarDays />, roles: ['super_admin', 'dept_admin', 'hr_admin', 'team_lead'] },
      { label: 'nav.leaveCalendar', path: '/hr/calendar', icon: <Calendar /> },
      { label: 'nav.attendance',    path: '/hr/attendance', icon: <Clock /> },
      { label: 'nav.documents',     path: '/hr/documents', icon: <FileText /> },
      { label: 'nav.compliance',    path: '/hr/compliance', icon: <ShieldCheck />, roles: ['super_admin', 'hr_admin'] },
      { label: 'nav.promotion',     path: '/hr/promotion', icon: <TrendingUp />, roles: ['super_admin', 'hr_admin'] },
      /*
       * Resignation is intentionally NOT role-gated in the sidebar — per
       * the v3 roadmap it is a self-service flow available to every employee
       * (regular employees see only their own resignation, HR sees the full
       * listing). The role-aware UI lives inside ResignationPage itself.
       */
      { label: 'nav.resignation',   path: '/hr/resignation', icon: <DoorOpen /> },
      { label: 'nav.termination',   path: '/hr/termination', icon: <UserX />, roles: ['super_admin', 'hr_admin'] },
      { label: 'nav.hrReports',     path: '/hr/reports', icon: <FileBarChart />, roles: ['super_admin', 'hr_admin'] },
    ],
  },
  {
    label: 'nav.work',
    icon: <CheckSquare size={18} />,
    moduleKey: 'projects',
    groupLabel: 'Work',
    children: [
      { label: 'nav.myTasks',      path: '/work/my-tasks', icon: <CheckSquare /> },
      { label: 'nav.projects',     path: '/work/projects', icon: <Briefcase /> },
      { label: 'nav.timeline',     path: '/work/timeline', icon: <GanttChart /> },
      { label: 'nav.timeTracking', path: '/work/time-tracking', icon: <Timer /> },
    ],
  },
  {
    label: 'nav.support',
    icon: <LifeBuoy size={18} />,
    moduleKey: 'support',
    children: [
      { label: 'nav.myTickets',       path: '/support/my-tickets', icon: <LifeBuoy /> },
      { label: 'nav.teamQueue',       path: '/support/team-queue', icon: <Users />, roles: ['super_admin', 'hr_admin', 'dept_admin', 'team_lead'] },
      { label: 'nav.allTickets',      path: '/support/all-tickets', icon: <Inbox />, roles: ['super_admin', 'hr_admin', 'dept_admin'] },
      { label: 'nav.slaDashboard',    path: '/support/sla-dashboard', icon: <AlarmClock />, roles: ['super_admin', 'hr_admin', 'dept_admin', 'team_lead'] },
      { label: 'nav.cannedResponses', path: '/support/macros', icon: <Zap />, roles: ['super_admin', 'hr_admin', 'dept_admin', 'team_lead'] },
      { label: 'nav.knowledgeBase',   path: '/support/knowledge-base', icon: <BookOpen /> },
    ],
  },
  {
    label: 'nav.company',
    icon: <Building2 size={18} />,
    groupLabel: 'Company',
    children: [
      { label: 'nav.announcements',  path: '/company/announcements', icon: <Megaphone /> },
      { label: 'nav.policies',       path: '/company/policies', icon: <ShieldCheck /> },
      { label: 'nav.events',         path: '/company/events', icon: <Calendar /> },
      { label: 'nav.directory',      path: '/company/directory', icon: <Users /> },
      { label: 'nav.accessRequests', path: '/company/access-requests', icon: <UserPlus />, roles: ['super_admin', 'admin', 'hr_admin', 'dept_admin'] },
      { label: 'nav.channels',       path: '/communication/channels', icon: <Hash /> },
      { label: 'nav.directMessages', path: '/communication/direct-messages', icon: <MessageSquare /> },
    ],
  },
  {
    label: 'nav.analytics',
    icon: <BarChart3 size={18} />,
    moduleKey: 'analytics',
    children: [
      { label: 'nav.overview',      path: '/analytics/overview', icon: <PieChart /> },
      { label: 'nav.customReports', path: '/analytics/reports', icon: <FileBarChart /> },
    ],
  },
  {
    label: 'nav.crm',
    icon: <Briefcase size={18} />,
    moduleKey: 'crm',
    groupLabel: 'CRM & Sales',
    children: [
      { label: 'nav.dashboard',    path: '/crm/dashboard', icon: <LayoutDashboard />, tint: TINT.sky },
      { label: 'nav.leads',        path: '/crm/leads', icon: <Funnel />, tint: TINT.amber, countKey: 'leads' },
      { label: 'nav.contacts',     path: '/crm/contacts', icon: <Contact />, tint: TINT.green, countKey: 'contacts' },
      { label: 'nav.companies',    path: '/crm/companies', icon: <Building2 />, tint: TINT.purple, countKey: 'companies' },
      { label: 'nav.deals',        path: '/crm/deals', icon: <Handshake />, tint: TINT.teal },
      { label: 'nav.products',     path: '/crm/products', icon: <Package />, tint: TINT.orange },
      { label: 'nav.activities',   path: '/crm/activities', icon: <Activity />, tint: TINT.red, countKey: 'activitiesOverdue' },
      { label: 'nav.salesReports', path: '/crm/reports', icon: <BarChart3 />, tint: TINT.violet },
      { label: 'nav.crmSettings',  path: '/crm/settings', icon: <Settings />, tint: TINT.slate, roles: ['super_admin', 'dept_admin'] },
    ],
  },
  {
    label: 'nav.marketing',
    icon: <Megaphone size={18} />,
    groupLabel: '',
    children: [
      { label: 'nav.mktDashboard',   path: '/marketing/dashboard', icon: <LayoutDashboard /> },
      { label: 'nav.campaigns',      path: '/marketing/campaigns', icon: <Megaphone /> },
      { label: 'nav.emailMarketing', path: '/marketing/email', icon: <Mail /> },
      { label: 'nav.sequences',      path: '/marketing/sequences', icon: <ListOrdered /> },
      { label: 'nav.leadSources',    path: '/marketing/lead-sources', icon: <Users /> },
    ],
  },
  {
    label: 'nav.finance',
    icon: <DollarSign size={18} />,
    moduleKey: 'finance',
    groupLabel: 'Finance',
    children: [
      { label: 'nav.myExpenses', path: '/finance/expenses', icon: <Receipt /> },
      { label: 'nav.invoices',   path: '/finance/invoices', icon: <FileText />, roles: ['super_admin', 'hr_admin', 'dept_admin', 'finance_admin'] },
      { label: 'nav.quotes',     path: '/finance/quotes', icon: <FileCheck />, roles: ['super_admin', 'hr_admin', 'finance_admin'] },
      { label: 'nav.arAging',    path: '/finance/ar-aging', icon: <Scale />, roles: ['super_admin', 'hr_admin', 'finance_admin'] },
      { label: 'nav.payments',   path: '/finance/payments', icon: <CreditCard />, roles: ['super_admin', 'hr_admin', 'dept_admin', 'finance_admin'] },
      { label: 'nav.budgets',    path: '/finance/budgets', icon: <Wallet />, roles: ['super_admin', 'hr_admin', 'dept_admin'] },
      { label: 'nav.approvals',  path: '/finance/approvals', icon: <BadgeCheck />, roles: ['super_admin', 'hr_admin', 'dept_admin', 'team_lead'] },
      { label: 'nav.reports',    path: '/finance/reports', icon: <FileBarChart />, roles: ['super_admin', 'hr_admin'] },
    ],
  },
  {
    label: 'nav.administration',
    icon: <Warehouse size={18} />,
    roles: ['super_admin', 'hr_admin', 'dept_admin'],
    children: [
      { label: 'nav.procurement',      path: '/admin/procurement', icon: <ShoppingCart /> },
      { label: 'nav.assets',           path: '/admin/assets', icon: <Warehouse /> },
      { label: 'nav.vendors',          path: '/admin/vendors', icon: <Truck /> },
      { label: 'nav.forms',            path: '/admin/forms', icon: <ClipboardList />, roles: ['super_admin', 'dept_admin'] },
    ],
  },
  {
    label: 'nav.owner',
    icon: <Crown size={18} />,
    roles: ['super_admin'],
    groupLabel: 'Owner',
    children: [
      { label: 'nav.ownerDashboard', path: '/owner/dashboard', icon: <LayoutDashboard /> },
      { label: 'nav.broadcast',      path: '/owner/broadcast', icon: <Radio /> },
    ],
  },
  {
    label: 'nav.admin',
    icon: <Settings size={18} />,
    roles: ['super_admin', 'hr_admin'],
    groupLabel: 'Admin',
    children: [
      { label: 'nav.holidayManagement', path: '/admin/holidays', icon: <Sun /> },
      { label: 'nav.leavePolicies',     path: '/admin/leave-policies', icon: <FileText /> },
      { label: 'nav.leaveBalances',     path: '/admin/leave-balances', icon: <Scale /> },
      { label: 'nav.users',             path: '/admin/users', icon: <Users />, roles: ['super_admin'] },
      { label: 'nav.access',            path: '/admin/access', icon: <Lock />, roles: ['super_admin'] },
      { label: 'nav.departments',       path: '/admin/departments', icon: <Building2 />, roles: ['super_admin'] },
      { label: 'nav.companySettings',   path: '/admin/company', icon: <Building />, roles: ['super_admin'] },
      { label: 'nav.billing',           path: '/admin/billing', icon: <CreditCard />, roles: ['super_admin'] },
      { label: 'nav.integrations',      path: '/admin/integrations', icon: <Puzzle />, roles: ['super_admin'] },
      { label: 'nav.auditLog',          path: '/admin/audit-log', icon: <ScrollText />, roles: ['super_admin'] },
      { label: 'nav.automation',        path: '/admin/automation', icon: <Workflow />, roles: ['super_admin'] },
      { label: 'nav.gdprRequests',      path: '/admin/gdpr', icon: <ShieldCheck />, roles: ['super_admin'] },
      { label: 'nav.setupChecklist',    path: '/admin/setup', icon: <ListChecks />, roles: ['super_admin'], tourAttr: 'setup-checklist' },
      { label: 'nav.settings',          path: '/settings', icon: <Settings /> },
    ],
  },
];

/**
 * Live counts for the CRM leaves. One batch of exact HEAD counts (no rows fetched,
 * so PostgREST's 1000-row cap is irrelevant), workspace-scoped + soft-delete
 * filtered exactly like the CRM pages. Overdue = not completed AND a due date in
 * the past (lib/activityFeed's isActivityOverdue, which the Activities page uses).
 * Fetched once per workspace, only when CRM is accessible; failures leave the count
 * absent (no badge) rather than showing a wrong 0.
 *
 * 2026-10-30 (CRM fix batch 2 item 5): the overdue count reads `activities`, the one
 * table the Activities page and the record drawers now share. It read the legacy
 * `crm_activities`, which nothing writes any more, so the badge would have stayed at 0.
 * `activities` has no is_deleted (deletes are hard).
 */
function useSidebarCounts(enabled: boolean) {
  const { workspaceId, settled } = useWorkspaceId();
  const [counts, setCounts] = useState<Partial<Record<CountKey, number>>>({});

  useEffect(() => {
    if (!enabled || !settled || !workspaceId) { setCounts({}); return; }
    let cancelled = false;
    (async () => {
      const nowIso = new Date().toISOString();
      const headCount = (table: string) =>
        supabase.from(table).select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId).eq('is_deleted', false);
      // eslint-disable-next-line no-restricted-syntax -- ArrayPattern of Promise.all count results; each result's error is handled in pick() below (r.error → count absent, never a silent empty).
      const [leads, contacts, companies, overdue] = await Promise.all([
        headCount('crm_leads'),
        headCount('crm_contacts'),
        headCount('crm_companies'),
        supabase.from('activities').select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspaceId).in('module_key', ACTIVITY_RECORD_MODULE_KEYS)
          .is('completed_at', null).lt('due_at', nowIso),
      ]);
      if (cancelled) return;
      const pick = (r: { count: number | null; error: unknown }) =>
        r.error ? undefined : (r.count ?? undefined);
      setCounts({
        leads: pick(leads),
        contacts: pick(contacts),
        companies: pick(companies),
        activitiesOverdue: pick(overdue),
      });
    })();
    return () => { cancelled = true; };
  }, [enabled, workspaceId, settled]);

  return counts;
}

/** "super_admin" → "Super Admin" for the footer. */
function roleLabel(role?: string): string {
  if (!role) return '';
  return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

interface SidebarProps {
  collapsed: boolean;
  /** Mobile: whether the overlay sidebar is currently open */
  mobileOpen?: boolean;
  /** True when the viewport is below the lg breakpoint */
  isMobile?: boolean;
  /** Called when a nav link is tapped on mobile to close the overlay */
  onMobileClose?: () => void;
  /**
   * Collapse / expand the sidebar, for the control the design puts in the Apps
   * header. **The same callback the top bar's hamburger uses** — not a second
   * state, or the two controls could disagree about one width.
   */
  onToggleCollapsed?: () => void;
}

/**
 * Wrapped in React.memo so the Sidebar only re-renders when its own props change.
 * Heavy operations (NAV filtering, section visibility) are memoised with useMemo.
 * Callbacks are stabilised with useCallback so child NavLinks don't re-render
 * due to new function references on every parent render.
 */
const Sidebar = memo(function Sidebar({
  collapsed,
  mobileOpen = false,
  isMobile = false,
  onMobileClose,
  onToggleCollapsed,
}: SidebarProps) {
  const appsCounts = useAppsRailCounts();
  const { profile, signOut } = useAuth();
  const { canAccess: canOpenModule } = useModules();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  /* Sidebar Style (Settings → Appearance): Glass / Solid Dark / Match BG. The
     attribute is the whole wiring — index.css owns the three looks. */
  const { sidebarStyle } = useTheme();

  /** Dock tile owning the current route (drives the context). */
  const activeDock = dockKeyForPath(location.pathname);

  /**
   * The module you were last ACTUALLY in. See `lastModule.ts`: nothing in this app
   * remembered it before 2026-10-06, so this is the whole of that memory.
   *
   * ⚠️ Written only from `activeDock`, never from the fallback below — recording a
   * fallback would make it self-perpetuating, and the real answer would never replace it.
   */
  const [remembered, setRemembered] = useState(() => readLastModule());
  useEffect(() => {
    if (!activeDock) return;
    rememberLastModule(activeDock);
    setRemembered(activeDock);
  }, [activeDock]);

  /**
   * WHICH module's sections to draw. On every normal page this is the route's own module
   * and nothing changes. On an App route — `/mail` and friends, which no dock tile owns —
   * it falls back to the remembered module, then to the first one you can access.
   */
  const moduleChoice = useMemo(
    () => chooseModuleSection(location.pathname, { remembered, canAccessModule: canOpenModule }),
    [location.pathname, remembered, canOpenModule],
  );

  /**
   * The dock item whose sections are on screen. Drives the leaf badge COLOUR, so the
   * fallback module's pages keep their tints instead of all going grey.
   */
  const sectionItem = moduleChoice.key ? DOCK.find(d => d.key === moduleChoice.key) : undefined;

  /**
   * Dock item owning the current route — drives the chip header (V2).
   *
   * ⛔ `fromRoute` IS THE WHOLE POINT. Lis: the module section on `/mail` shows the group
   * header, the module and its pages, with **no active page** and **no "Active module"
   * card**. A fallback is sections without a location, so it must not claim one.
   */
  const activeItem = moduleChoice.fromRoute ? sectionItem : undefined;

  /** Live CRM counts for the leaf badges — only fetched when CRM is accessible. */
  const counts = useSidebarCounts(canOpenModule('crm'));

  /** Signed-in person's display name + initials + role for the footer chip. */
  const userName = profile?.full_name?.trim() || 'User';
  const userRole = roleLabel(profile?.role);
  const userInitials = userName
    .replace(/[^A-Za-z0-9 ]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0]?.toUpperCase())
    .slice(0, 2)
    .join('') || 'U';

  /** Footer account menu (Profile / Settings / Language / Sign out) — fills the
      dead space. Language moved here from the removed dock account tile: this
      menu is now the app's ONE account surface, and the picker was the only
      working locale switcher (the Settings page's select is decorative). */
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const currentLocale = getLocale();
  const footRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!accountMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (footRef.current && !footRef.current.contains(e.target as Node)) {
        setAccountMenuOpen(false);
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [accountMenuOpen]);

  /** Sections that are currently expanded. Default open if a child is active. */
  const [openSections, setOpenSections] = useState<string[]>(() => {
    return NAV.filter(s =>
      s.children?.some(c => location.pathname.startsWith(c.path))
    ).map(s => s.label);
  });

  /** Keep sections open when navigating. */
  useEffect(() => {
    NAV.forEach(s => {
      if (s.children?.some(c => location.pathname.startsWith(c.path))) {
        setOpenSections(prev => prev.includes(s.label) ? prev : [...prev, s.label]);
      }
    });
  }, [location.pathname]);

  /**
   * ⛔ A FALLBACK SECTION OPENS ITSELF, BECAUSE NOTHING ELSE CAN OPEN IT.
   *
   * The decision is `openSectionsWithFallback`, where it can be tested; this is only the
   * wiring. It runs when the CHOSEN MODULE changes, not on every render — press the
   * chevron and the section closes and stays closed.
   */
  useEffect(() => {
    setOpenSections(prev => openSectionsWithFallback(prev, moduleChoice));
  }, [moduleChoice]);

  const toggleSection = useCallback((label: string) =>
    setOpenSections(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    ), []);

  /** Filter a list of children/sections by the current user's role. */
  const canAccess = useCallback((roles?: UserRole[]) =>
    !roles || (profile && roles.includes(profile.role as UserRole)), [profile]);

  /**
   * Hide a menu entry whose PAGE the router would refuse — the menu and the
   * router share moduleForPath, so they can never disagree about ownership.
   */
  const moduleAllows = useCallback((path?: string) => {
    if (!path) return true;
    const key = moduleForPath(path);
    return !key || canOpenModule(key);
  }, [canOpenModule]);

  /** Role + module pre-filtered NAV (same as before the redesign). */
  const visibleSections = useMemo(() =>
    NAV.filter(section => {
      if (!canAccess(section.roles)) return false;
      if (section.moduleKey && !canOpenModule(section.moduleKey)) return false;
      if (!moduleAllows(section.path)) return false;
      return true;
    }), [canAccess, canOpenModule, moduleAllows]);

  /**
   * The sections of the module chosen above — the route's own, or the fallback.
   *
   * ⚠️ It can still come back EMPTY, and that is honest rather than a hole: a person with
   * access to nothing has no module section to draw. What it may no longer do is come back
   * empty merely because the ROUTE is an App. `sidebarModuleSection.test.ts` holds both
   * arms, so the difference cannot quietly collapse again.
   */
  const contextSections = useMemo(() => {
    const labels = sectionLabelsForChoice(moduleChoice);
    if (labels.length === 0) return [];
    return visibleSections.filter(s => labels.includes(s.label));
  }, [visibleSections, moduleChoice]);

  /** Click a mobile module tile → go to its default path + close the overlay. */
  const handleDockNav = (path: string) => {
    if (isMobile && onMobileClose) onMobileClose();
    navigate(path);
  };

  /**
   * Mobile: sidebar is an overlay under the dock (top-16), slides in from the left.
   * Desktop: always on-screen under the dock, width switches 64px ↔ 256px.
   */
  const asideClasses = isMobile
    ? `fixed top-16 left-0 bottom-0 flex flex-col z-50 w-64 transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`
    : /* transition-[width], NOT transition-all: `all` put a 300ms transition on every
         property of the blurred panel itself — one more way a hover-adjacent change
         kept the glass layer animating/repainting (white-streak round 2). Width is
         the only property the collapse toggle animates. */
      `fixed top-16 left-0 bottom-0 flex flex-col transition-[width] duration-300 z-30 ${
        collapsed ? 'w-16' : 'w-64'
      }`;

  /** Close the mobile overlay after a nav link is clicked */
  const handleNavClick = () => {
    if (isMobile && onMobileClose) onMobileClose();
  };

  const renderSection = (section: NavSection) => {
    /* Direct link (no children) */
    if (section.path) {
      return (
        <React.Fragment key={section.label}>
          {!collapsed && section.groupLabel && (
            <div className="pq-nav-label">{section.groupLabel}</div>
          )}
          <NavLink
            to={section.path}
            onClick={handleNavClick}
            className={({ isActive }) =>
              `pq-nav-item ${collapsed ? 'pq-collapsed' : ''} ${isActive ? 'active' : ''}`
            }
            title={collapsed ? t(section.label) : undefined}
          >
            {section.icon}
            {!collapsed && <span className="pq-label">{t(section.label)}</span>}
          </NavLink>
        </React.Fragment>
      );
    }

    /* Expandable section */
    const visibleChildren = (section.children || [])
      .filter(c => canAccess(c.roles) && moduleAllows(c.path))
      // The "Access requests" item is part of the multi-tenant sign-up work —
      // hidden entirely when REACT_APP_SIGNUP_ENABLED is OFF (the default).
      .filter(c => c.path !== '/company/access-requests' || isSignupEnabled());
    if (visibleChildren.length === 0) return null;
    const isOpen = openSections.includes(section.label);
    const anyChildActive = visibleChildren.some(c => location.pathname.startsWith(c.path));

    return (
      <React.Fragment key={section.label}>
        {!collapsed && section.groupLabel && (
          <div className="pq-nav-label">{section.groupLabel}</div>
        )}
        <button
          type="button"
          onClick={() => !collapsed && toggleSection(section.label)}
          className={`pq-nav-item ${collapsed ? 'pq-collapsed' : ''} ${anyChildActive ? 'pq-parent-active' : ''}`}
          title={collapsed ? t(section.label) : undefined}
        >
          {section.icon}
          {!collapsed && <span className="pq-label">{t(section.label)}</span>}
          {!collapsed && (
            isOpen
              ? <ChevronDown size={14} className="pq-chevron" />
              : <ChevronRight size={14} className="pq-chevron" />
          )}
        </button>

        {/* ⛔ THE PAGES STAY ON SCREEN WHEN THE SIDEBAR IS COLLAPSED.
            *Approved design, 2026-10-06, in its own words: "The strip keeps every
            line as an icon: Webmail, My tasks, Chat, Directory at the top, then
            CRM & Sales and its pages."*

            Before this the condition was `!collapsed && isOpen`, so collapsing the
            sidebar left the module as a single 64px glyph with its pages gone —
            every destination below Apps unreachable without expanding first.

            ⚠️ COLLAPSED IGNORES `isOpen`, AND THAT IS NOT AN OVERSIGHT. The chevron
            that sets `isOpen` is not rendered at this width and the section button
            does not toggle (`onClick={() => !collapsed && toggleSection(...)}`), so
            honouring a flag nobody can change would hide the pages for anyone who
            happened to collapse the section first — and leave them no control to
            get them back. */}
        {(collapsed || isOpen) && (
          <div className="pq-subnav" data-collapsed={String(collapsed)}>
            {visibleChildren.map(child => {
              /* Badge colour: explicit tint, else the active module's colour,
                 else neutral (grey). The active state recolours it to accent. */
              const badgeColor = child.neutral ? null : (child.tint ?? sectionItem?.color ?? null);
              const countVal = child.countKey ? counts[child.countKey] : undefined;
              const alert = child.countKey === 'activitiesOverdue' && (countVal ?? 0) > 0;
              return (
                <NavLink
                  key={child.path}
                  to={child.path}
                  onClick={handleNavClick}
                  {...(child.tourAttr ? { 'data-tour': child.tourAttr } : {})}
                  className={({ isActive }) =>
                    `pq-subitem ${collapsed ? 'pq-collapsed' : ''} ${isActive ? 'active' : ''}`}
                  title={collapsed ? t(child.label) : undefined}
                  aria-label={collapsed
                    ? (countVal != null ? `${t(child.label)} · ${countVal}` : t(child.label))
                    : undefined}
                >
                  {child.icon ? (
                    <span
                      className={`pq-badge ${badgeColor ? '' : 'pq-badge-neutral'}`}
                      style={badgeColor ? ({ ['--badge-c' as string]: badgeColor }) : undefined}
                    >
                      {child.icon}
                    </span>
                  ) : (
                    <span className="pq-bullet" />
                  )}
                  {/* Collapsed: the glyph alone, with the name as its hover label —
                      the same mechanism the section rows above already use, so the
                      word leaves the screen and never the accessibility tree. */}
                  {!collapsed && <span className="pq-sublabel">{t(child.label)}</span>}
                  {!collapsed && countVal != null && (
                    <span className={`pq-count ${alert ? 'pq-count-alert' : ''}`}>{countVal}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <aside
      data-tour="sidebar"
      data-sidebar-style={sidebarStyle.id}
      className={`${asideClasses} pq-side`}
    >
      {/* ── Mobile: horizontal module switcher ── */}
      {isMobile && (
        <div className="pq-dock-scroll">
          {/* Same accessibility rule as the module section — one definition, not two.
              It was copied here and in `chooseModuleSection`; this is the shared one. */}
          {DOCK.filter(item => dockItemAccessible(item, canOpenModule)).map(item => {
            const on = activeDock === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleDockNav(item.defaultPath)}
                className={`pq-dock-tile ${on ? 'on' : ''}`}
                style={{ color: item.color }}
              >
                {item.icon}
                <span>{t(item.labelKey, { defaultValue: item.fallback })}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── APPS: the floor. One rail, two groups. ──────────────────────────
          The design's own sentence: "Apps is the floor and never changes; only
          the module section below it swaps." EVERYTHING that changes with the
          module goes below it — including the "Active module" chip, which is a
          label FOR that changing section and reads as its heading.

          ⚠️ It shipped the other way round in #337 and had to be moved: the chip
          sat above Apps, so the first thing in the rail was the thing that
          changes and the fixed floor appeared to belong to it. It sits ABOVE the active-module chip and
          OUTSIDE the context nav's scroll container, so it stays put while a long module
          section scrolls beneath it.

          Hidden on mobile, where the horizontal module switcher already occupies
          the top of this overlay and a second list of destinations would bury
          it. */}
      {!isMobile && (
        <AppsRail
          counts={appsCounts}
          collapsed={collapsed}
          /* The design draws the collapse control in this header, top right.
             Absent on mobile, where the rail itself is absent. */
          onToggleCollapsed={onToggleCollapsed}
        />
      )}
      {/* ── Active-module chip header (V2 mockup .ctx-mod) ──
          ⚠️ MOVED INTO `ActiveModuleChip`, NOT CHANGED. The markup, the classes
          and the tint are the same; it became a component so `MailSidebar` can
          render the same one instead of copying it. `/mail` is an own-rail route,
          so this `Sidebar` never renders there and Mail had no chip at all. */}
      {!isMobile && !collapsed && activeItem && (
        <ActiveModuleChip
          icon={activeItem.icon}
          color={activeItem.color}
          name={t(activeItem.labelKey, { defaultValue: activeItem.fallback })}
        />
      )}


      {/* ── Context navigation ── */}
      <nav className="flex-1 overflow-y-auto sidebar-nav-scroll" style={{ minHeight: 0 }}>
        {contextSections.map(renderSection)}
      </nav>

      {/* ── Account footer: avatar + name + role + switcher menu (fills the
             dead space below the nav; opens Profile / Settings / Sign out). ── */}
      {!collapsed && (
        <div className="pq-ctx-foot-wrap" ref={footRef}>
          {accountMenuOpen && (
            <div className="pq-foot-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="pq-foot-menu-item"
                onClick={() => { setAccountMenuOpen(false); handleNavClick(); navigate('/profile'); }}
              >
                <User size={15} />
                {t('common.profile', { defaultValue: 'Profile' })}
              </button>
              <button
                type="button"
                role="menuitem"
                className="pq-foot-menu-item"
                onClick={() => { setAccountMenuOpen(false); handleNavClick(); navigate('/settings'); }}
              >
                <Settings size={15} />
                {t('common.settings', { defaultValue: 'Settings' })}
              </button>
              <button
                type="button"
                role="menuitem"
                className="pq-foot-menu-item"
                aria-expanded={langOpen}
                onClick={() => setLangOpen(o => !o)}
              >
                <Globe size={15} />
                <span className="flex-1 text-left">{t('topbar.language', { defaultValue: 'Language' })}</span>
                <span className="pq-foot-lang-code">{currentLocale}</span>
              </button>
              {langOpen && (
                <div className="pq-foot-lang-list">
                  {SUPPORTED_LOCALES.map(code => (
                    <button
                      key={code}
                      type="button"
                      role="menuitem"
                      className={`pq-foot-lang-item ${currentLocale === code ? 'on' : ''}`}
                      onClick={() => {
                        setLocale(code as LocaleCode);
                        setLangOpen(false);
                        setAccountMenuOpen(false);
                      }}
                    >
                      <span>{t(`lang.${code}`)}</span>
                      {currentLocale === code && <span aria-hidden="true">✓</span>}
                    </button>
                  ))}
                </div>
              )}
              <div className="pq-foot-menu-sep" />
              <button
                type="button"
                role="menuitem"
                className="pq-foot-menu-item pq-foot-menu-danger"
                onClick={() => { setAccountMenuOpen(false); signOut(); }}
              >
                <SignOutIcon size={15} />
                {t('common.logout', { defaultValue: 'Sign out' })}
              </button>
            </div>
          )}
          <button
            type="button"
            className={`pq-ctx-foot ${accountMenuOpen ? 'open' : ''}`}
            onClick={() => setAccountMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
          >
            {/* The last hand-rolled avatar in the app. `.pq-wschip` still supplies the
                gradient and type; the box comes from the shared component at the same
                30px / 9px it has always been. */}
            <Avatar initials={userInitials} size={30} radius={9} className="pq-wschip" />
            <span className="pq-foot-meta">
              <span className="pq-wsname">{userName}</span>
              {userRole && <span className="pq-wsrole">{userRole}</span>}
            </span>
            <svg className="pq-foot-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
            </svg>
          </button>
        </div>
      )}
    </aside>
  );
});

export default Sidebar;
